import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UserCheck, Mail, Phone, Send, CalendarClock, MapPin, Video } from "lucide-react";

const err = (e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Errore");
const MODES = [["presenza", "In presenza", MapPin], ["video", "Videochiamata", Video], ["telefono", "Telefono", Phone]];
const TONE = { richiesto: "bg-amber-100 text-amber-800", confermato: "bg-emerald-100 text-emerald-800", rifiutato: "bg-red-100 text-red-700", svolto: "bg-stone-200 text-stone-700", annullato: "bg-stone-100 text-stone-500" };
const dt = (iso) => new Date(iso).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" });

function TutorCard({ data }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const send = useMutation({
    mutationFn: async () => (await api.post("/tutor-area/me/message", { body })).data,
    onSuccess: () => { toast.success("Messaggio inviato al tutor"); setBody(""); qc.invalidateQueries({ queryKey: ["tutor-me"] }); },
    onError: err,
  });
  const t = data.tutor;
  if (!t) return <Card className="p-6 border border-stone-200 rounded-xl shadow-none text-sm text-stone-600" data-testid="tutor-none">Non hai ancora un tutor assegnato. Per informazioni scrivi alla segreteria dalla sezione Messaggi o Help Desk.</Card>;
  return (
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none space-y-4" data-testid="tutor-card">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-[#1C352D] text-white grid place-items-center"><UserCheck size={20}/></div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-stone-500">Il tuo tutor</div>
          <div className="text-lg font-semibold text-[#1C352D]" data-testid="tutor-name">{t.name}</div>
        </div>
      </div>
      <Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder={`Scrivi un messaggio a ${t.name}…`} data-testid="tutor-message-input"/>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <span className="text-[11px] text-stone-500">Il tutor riceve il messaggio via email e può risponderti direttamente.{data.thread_id && <> Lo storico è in <Link to={`/portale/messaggi/${data.thread_id}`} className="underline">Messaggi</Link>.</>}</span>
        <Button disabled={!body.trim() || send.isPending} onClick={() => send.mutate()} className="rounded-full bg-[#1C352D] hover:bg-[#14271f]" data-testid="tutor-message-send"><Send size={14} className="mr-1.5"/>{send.isPending ? "Invio…" : "Invia"}</Button>
      </div>
    </Card>
  );
}

function MeetingForm({ disabled }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState("presenza");
  const [preferred, setPreferred] = useState("");
  const [reason, setReason] = useState("");
  const req = useMutation({
    mutationFn: async () => (await api.post("/tutor-area/me/meetings", { mode, preferred, reason })).data,
    onSuccess: () => { toast.success("Richiesta di colloquio inviata alla segreteria"); setPreferred(""); setReason(""); qc.invalidateQueries({ queryKey: ["tutor-meetings"] }); },
    onError: err,
  });
  return (
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none space-y-3" data-testid="meeting-form">
      <div className="text-sm font-medium flex items-center gap-2"><CalendarClock size={15}/> Richiedi un colloquio</div>
      <div className="grid grid-cols-3 gap-2">
        {MODES.map(([v, l, Icon]) => (
          <button key={v} type="button" onClick={() => setMode(v)} data-testid={`meeting-mode-${v}`}
            className={`rounded-xl border px-2 py-2.5 text-xs flex flex-col items-center gap-1 transition-colors ${mode === v ? "border-[#1C352D] bg-[#1C352D] text-white" : "border-stone-200 hover:border-stone-400"}`}>
            <Icon size={15}/>{l}
          </button>
        ))}
      </div>
      <Input value={preferred} onChange={(e) => setPreferred(e.target.value)} placeholder="Quando preferisci? (es. martedì pomeriggio, dopo le 18)" data-testid="meeting-preferred"/>
      <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Di cosa vorresti parlare? (facoltativo)" data-testid="meeting-reason"/>
      <div className="flex justify-end">
        <Button disabled={disabled || !preferred.trim() || req.isPending} onClick={() => req.mutate()} className="rounded-full bg-[#E2725B] hover:bg-[#C95F49]" data-testid="meeting-submit">Invia richiesta</Button>
      </div>
      {disabled && <p className="text-[11px] text-amber-700">Hai già una richiesta in attesa: la segreteria ti confermerà data e ora.</p>}
    </Card>
  );
}

function MeetingList({ items }) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: async (id) => (await api.patch(`/tutor-area/me/meetings/${id}/cancel`)).data,
    onSuccess: () => { toast.success("Colloquio annullato"); qc.invalidateQueries({ queryKey: ["tutor-meetings"] }); },
    onError: err,
  });
  if (!items.length) return null;
  return (
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none" data-testid="meeting-list">
      <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-3">I tuoi colloqui</div>
      <ul className="divide-y divide-stone-100">
        {items.map((m) => (
          <li key={m.id} className="py-3 flex flex-wrap items-start gap-3" data-testid={`meeting-${m.id}`}>
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-medium">{m.scheduled_at && m.status === "confermato" ? dt(m.scheduled_at) : m.preferred} · {m.mode_label}</div>
              {m.where && m.status === "confermato" && <div className="text-xs text-stone-600 mt-0.5 break-all">{m.where}</div>}
              {m.staff_note && <div className="text-xs text-stone-500 mt-0.5">{m.staff_note}</div>}
              <div className="text-[11px] text-stone-400 mt-0.5">Richiesto il {new Date(m.created_at).toLocaleDateString("it-IT")}</div>
            </div>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${TONE[m.status]}`} data-testid={`meeting-status-${m.id}`}>{m.status_label}</span>
            {["richiesto", "confermato"].includes(m.status) && (
              <Button size="sm" variant="ghost" className="text-xs text-stone-500" onClick={() => window.confirm("Annullare il colloquio?") && cancel.mutate(m.id)} data-testid={`meeting-cancel-${m.id}`}>Annulla</Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function PortaleTutor() {
  const { data, error } = useQuery({ queryKey: ["tutor-me"], queryFn: async () => (await api.get("/tutor-area/me")).data, retry: false });
  const { data: meetings = [] } = useQuery({ queryKey: ["tutor-meetings"], queryFn: async () => (await api.get("/tutor-area/me/meetings")).data, enabled: !!data });
  if (error) return <div className="p-10 text-sm text-stone-500" data-testid="tutor-not-modular">{error.response?.data?.detail || "Sezione non disponibile"}</div>;
  if (!data) return <div className="p-10 text-sm text-stone-400">Caricamento…</div>;
  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[900px] mx-auto space-y-5" data-testid="portal-tutor-page">
      <PageHeader title="Il mio tutor" subtitle="Scrivi al tuo tutor o chiedi un colloquio: la segreteria ti confermerà data e ora."/>
      <TutorCard data={data}/>
      <MeetingForm disabled={meetings.some((m) => m.status === "richiesto")}/>
      <MeetingList items={meetings}/>
    </div>
  );
}
